import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TypographyProps } from '@material-ui/core';
import { useRouteMatch } from 'react-router-dom';

import { appConfigSelector } from 'reducers/app.reducer';
import {
  StyledCopyright,
  StyledDivider,
  StyledLink,
  StyledTypography,
} from './styles';

const today = new Date();

/*
This component is used on multiple pages of the application:
- on all pages as a footer
- on /auth page
- on /auth/subscribe page
- on /auth/reset-password page
- on /auth/forgot-password page
- on /auth/create/password page
*/
export default function Legals({
  isStoreEnabled,
  textProps = { align: 'center' },
}: {
  isStoreEnabled?: boolean;
  textProps?: TypographyProps;
}) {
  const { t } = useTranslation();

  const isAuthPages = !!useRouteMatch({
    path: '/auth',
    strict: true,
    sensitive: true,
  });

  const {
    userConditionsPath,
    salesConditionsPath,
    programUrl,
    creationDate,
    companyName,
    legalMentionsPath,
    dataProtectionsPath,
    cookiesConditionsPath,
  } = useSelector(appConfigSelector);

  const currentYear = today.getFullYear().toString();
  const isCreationDateDiffToCurrentYear = currentYear !== creationDate;

  return (
    <React.Fragment>
      <StyledTypography variant="h5" {...textProps}>
        {!isAuthPages && userConditionsPath && (
          <React.Fragment>
            <StyledLink
              href={`${programUrl}${userConditionsPath}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('footer.general_terms_and_conditions')}
            </StyledLink>
            {salesConditionsPath && <StyledDivider />}
          </React.Fragment>
        )}
        {isStoreEnabled && salesConditionsPath && (
          <StyledLink
            href={`${programUrl}${salesConditionsPath}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('footer.sales_conditions')}
          </StyledLink>
        )}
      </StyledTypography>
      <StyledTypography color="primary" variant="h5" {...textProps}>
        {legalMentionsPath && (
          <React.Fragment>
            <StyledLink
              href={`${programUrl}${legalMentionsPath}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('footer.legal_notice')}
            </StyledLink>
            {(dataProtectionsPath || cookiesConditionsPath) && (
              <StyledDivider />
            )}
          </React.Fragment>
        )}
        {dataProtectionsPath && (
          <React.Fragment>
            <StyledLink
              href={`${programUrl}${dataProtectionsPath}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('footer.personal_data_policy')}
            </StyledLink>
            {cookiesConditionsPath && <StyledDivider />}
          </React.Fragment>
        )}
        {cookiesConditionsPath && (
          <StyledLink
            href={`${programUrl}${cookiesConditionsPath}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('footer.cookies_policy')}
          </StyledLink>
        )}
      </StyledTypography>
      {companyName && creationDate && (
        <StyledCopyright color="primary" variant="h5" {...textProps}>
          {t('footer.copyright')} &copy; {companyName} {creationDate} -{' '}
          {isCreationDateDiffToCurrentYear && `${currentYear} -`}{' '}
          {t('footer.all_rights_reserved')}
        </StyledCopyright>
      )}
    </React.Fragment>
  );
}
